/**
 * Static metadata about the Drishti MCP tool surface.
 *
 * This is the single source of truth for:
 *  1. `search_tools` ranking — so the model can discover relevant MCP tools
 *     without every MCP schema being loaded into the prompt.
 *  2. The REST -> MCP automatic-fallback map (`capabilityMap`) — used when a
 *     REST call fails with a rate-limit/quota error and an equivalent MCP tool
 *     exists.
 *
 * Kept hand-authored and small on purpose. If the MCP server adds tools, they
 * still work when activated by name; they just won't rank in `search_tools`
 * until listed here.
 */

export type McpCategory =
  | 'sectoral'
  | 'price-volume'
  | 'movers'
  | 'docs'
  | 'events'
  | 'daily-summary'
  | 'symbols'
  | 'prompts'
  | 'overlap';

export interface McpCatalogEntry {
  /** MCP tool name as exposed by the server. */
  name: string;
  category: McpCategory;
  /** Lowercase keywords used for deterministic ranking. */
  keywords: string[];
  /** One-line summary shown to the model. */
  summary: string;
}

/**
 * MCP-only and overlap capabilities. MCP-only entries unlock functionality the
 * REST API does not expose; overlap entries mirror REST tools and serve as
 * fallback targets.
 */
export const MCP_CATALOG: McpCatalogEntry[] = [
  // --- MCP-only capabilities ---
  {
    name: 'get_sectoral_data',
    category: 'sectoral',
    keywords: ['sector', 'sectoral', 'industry', 'theme', 'sector performance', 'index'],
    summary: 'Sectoral / industry / theme performance data across the Indian market.',
  },
  {
    name: 'get_sector_wise_companies',
    category: 'sectoral',
    keywords: ['sector', 'companies', 'constituents', 'members', 'industry', 'list companies in sector'],
    summary: 'List companies belonging to a given sector / industry / theme.',
  },
  {
    name: 'get_top_movers',
    category: 'movers',
    keywords: ['top movers', 'gainers', 'losers', 'top gainers', 'top losers', 'biggest movers'],
    summary: 'Top gaining and losing stocks for the session.',
  },
  {
    name: 'get_top_traded',
    category: 'movers',
    keywords: ['top traded', 'most traded', 'volume leaders', 'turnover', 'most active'],
    summary: 'Most actively traded stocks by volume / turnover.',
  },
  {
    name: 'get_52w_data',
    category: 'price-volume',
    keywords: ['52 week', '52w', 'high', 'low', 'yearly high', 'yearly low', '52 week high', '52 week low'],
    summary: '52-week high / low data for symbols.',
  },
  {
    name: 'get_price_and_volume',
    category: 'price-volume',
    keywords: ['price', 'volume', 'ohlc', 'candles', 'quote', 'price and volume', 'trading data'],
    summary: 'Price and volume time-series data for a symbol.',
  },
  {
    name: 'get_price_and_volume_since',
    category: 'price-volume',
    keywords: ['price', 'volume', 'history', 'since', 'historical', 'time series', 'from date'],
    summary: 'Price and volume history for a symbol since a given date.',
  },
  {
    name: 'get_daily_summary',
    category: 'daily-summary',
    keywords: ['daily summary', 'market summary', 'digest', 'today', 'market recap', 'overview'],
    summary: 'Generated daily market summary / digest.',
  },
  {
    name: 'get_doc',
    category: 'docs',
    keywords: ['document', 'doc', 'fetch document', 'read doc', 'pdf', 'attachment'],
    summary: 'Fetch a specific Drishti document by id.',
  },
  {
    name: 'search_docs',
    category: 'docs',
    keywords: ['search documents', 'search docs', 'find document', 'document search', 'knowledge'],
    summary: 'Search Drishti documents by query.',
  },
  {
    name: 'get_events',
    category: 'events',
    keywords: ['events', 'corporate events', 'calendar', 'board meeting', 'upcoming events'],
    summary: 'Corporate / market events feed.',
  },
  {
    name: 'resolve_symbols',
    category: 'symbols',
    keywords: ['resolve', 'resolve symbol', 'resolve symbols', 'symbol lookup', 'find symbol', 'ticker', 'name to symbol', 'which symbol', 'ambiguous', 'company name'],
    summary: 'Resolve free-text company names / phrases to Drishti symbols.',
  },
  {
    name: 'get_company_research_prompt',
    category: 'prompts',
    keywords: ['research prompt', 'company research', 'prompt template', 'guide'],
    summary: 'Research-prompt template for a company.',
  },
  {
    name: 'get_earnings_concall_prompt',
    category: 'prompts',
    keywords: ['earnings prompt', 'concall prompt', 'prompt template', 'transcript analysis'],
    summary: 'Prompt template for earnings / concall analysis.',
  },
  {
    name: 'get_market_development_prompt',
    category: 'prompts',
    keywords: ['market development prompt', 'prompt template', 'market analysis'],
    summary: 'Prompt template for market-development analysis.',
  },

  // --- Overlap capabilities (also served by REST; used for fallback) ---
  {
    name: 'get_news',
    category: 'overlap',
    keywords: ['news', 'headlines', 'latest news', 'company news'],
    summary: 'News feed (REST-equivalent; MCP fallback for /v1/news).',
  },
  {
    name: 'search_news',
    category: 'overlap',
    keywords: ['search news', 'find news', 'news about', 'topic news'],
    summary: 'Search news by query.',
  },
  {
    name: 'get_announcements',
    category: 'overlap',
    keywords: ['announcements', 'filings', 'disclosures', 'corporate filings'],
    summary: 'Exchange announcements (REST-equivalent; MCP fallback for /v1/announcements).',
  },
  {
    name: 'search_announcements',
    category: 'overlap',
    keywords: ['search announcements', 'search filings', 'filing type'],
    summary: 'Search announcements by query / theme.',
  },
  {
    name: 'get_concalls',
    category: 'overlap',
    keywords: ['concall', 'conference call', 'earnings call'],
    summary: 'Conference calls (REST-equivalent; MCP fallback for /v1/concalls).',
  },
  {
    name: 'list_earnings',
    category: 'overlap',
    keywords: ['earnings', 'results', 'quarterly results'],
    summary: 'Earnings index (REST-equivalent; MCP fallback for /v1/earnings).',
  },
];

/** REST endpoint -> equivalent MCP tool + argument transform, for auto-fallback. */
export interface FallbackMapping {
  mcpTool: string;
  /** Transform REST query params into MCP tool arguments. */
  transformArgs: (params: Record<string, unknown>) => Record<string, unknown>;
}

/**
 * Only overlap capabilities have fallback mappings. Transforms are conservative:
 * they forward the common fields (symbols, date range, paging) and let the MCP
 * server apply its own defaults for anything unmapped.
 */
export const capabilityMap: Record<string, FallbackMapping> = {
  '/v1/news': {
    mcpTool: 'get_news',
    transformArgs: (p) => pickDefined({ symbols: p.symbols, from: p.from, to: p.to, limit: p.limit }),
  },
  '/v1/announcements': {
    mcpTool: 'get_announcements',
    transformArgs: (p) =>
      pickDefined({ symbols: p.symbols, categories: p.categories, from: p.from, to: p.to, limit: p.limit }),
  },
  '/v1/concalls': {
    mcpTool: 'get_concalls',
    transformArgs: (p) => pickDefined({ symbols: p.symbols, from: p.from, to: p.to, limit: p.limit }),
  },
  '/v1/earnings': {
    mcpTool: 'list_earnings',
    transformArgs: (p) => pickDefined({ symbols: p.symbols, from: p.from, to: p.to, limit: p.limit }),
  },
  '/v1/symbols/metadata': {
    mcpTool: 'get_symbols',
    transformArgs: (p) => pickDefined({ symbols: p.symbols, scrip_codes: p.scrip_codes }),
  },
};

function pickDefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null) out[k] = v;
  }
  return out;
}

/**
 * Rank catalog entries against a free-text query. Deterministic scoring:
 *   exact keyword hit (3) > category-name token hit (2) > summary token overlap (1).
 * Returns entries sorted by descending score, ties broken by catalog order.
 */
export function rankMcpTools(query: string, limit: number): McpCatalogEntry[] {
  const q = query.toLowerCase();
  const qTokens = new Set(q.split(/[^a-z0-9]+/).filter((t) => t.length > 2));

  const scored = MCP_CATALOG.map((entry, index) => {
    let score = 0;
    for (const kw of entry.keywords) {
      if (q.includes(kw)) score += 3;
    }
    if (qTokens.has(entry.category.replace(/-/g, ''))) score += 2;
    for (const token of entry.summary.toLowerCase().split(/[^a-z0-9]+/)) {
      if (token.length > 2 && qTokens.has(token)) score += 1;
    }
    return { entry, score, index };
  }).filter((s) => s.score > 0);

  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored.slice(0, Math.max(1, limit)).map((s) => s.entry);
}
